import { useContext, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useMeterType } from 'hooks/useMeterType';
import { usePaymentMethod } from 'hooks/usePaymentMethod';
import { useIsMoveInEligible } from 'hooks/useIsMoveInEligible';
import { useSupplyStatus } from 'hooks/useSupplyStatus';
import { XStateContext } from 'components/app/AppContext';
import { useIsNewToBrand } from 'hooks/useIsNewToBrand';
import { selectHasPsr } from 'state/psr/psr-slice';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { selectValidationRequired } from 'state/meterpoint-validation/meterpoint-validation-slice';
import { usePaymentOptions } from 'hooks/usePaymentOptions';
import { PAYMENT_METHODS } from 'config/app-config';
import { useHasMixedMeters } from 'hooks/useHasMixedMeters';
import { useEvTariffInformation } from 'hooks/useEvTariffInformation';
import { useMeterExceptions } from 'components/direct-sales/select-options/hooks/useMeterExceptions';
import { useIsUpsell } from 'hooks/useIsUpsell';
import { useMeterValidation } from 'hooks/useMeterValidation';
import { useExperiments } from 'hooks/useExperiments';

export const useComputeGuards = () => {
  const { hasAnyMeterValidationRequired } = useMeterValidation();
  const {
    isUpsellEnabled,
    isUpsellEnabledMeterValidationRequired
  } = useIsUpsell();
  const currentGuardsRef = useRef({});
  const stateMachineContext = useContext(XStateContext);
  const { allowJourneyMoveIn } = useFlags();
  const { releaseAbandonedCart } = useExperiments();
  const paymentOptions = usePaymentOptions();
  const { hasMeterExceptions } = useMeterExceptions();

  const {
    isBGCustomer,
    isBGECustomer,
    isRelatedElectricityMeter,
    hasElectricityTOUMeter,
    hasRelatedElectricityMeters
  } = useMeterType();
  const {
    isMonthlyDirectDebit,
    hasMultipleDirectDebitTypes,
    isAnyDirectDebit
  } = usePaymentMethod();
  const { showEvTariffInformation } = useEvTariffInformation();
  const hasMixedMeters = useHasMixedMeters();
  const hasPSR = useSelector(selectHasPsr, shallowEqual);
  const meterValidationRequired = useSelector(
    selectValidationRequired,
    shallowEqual
  );
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const isNewToBrand = useIsNewToBrand();

  const isMoveInEligible = useIsMoveInEligible();

  const isMeterValidationRequired = Object.values(meterValidationRequired).some(
    (v) => v
  );

  const allowAbandonedCart = !!(
    releaseAbandonedCart && !hasRelatedElectricityMeters
  );

  const {
    isNepSplitSupply,
    isSapSplitSupply,
    isMixedNepSap
  } = useSupplyStatus();

  const isPrePayOnly =
    paymentOptions.length === 1 &&
    paymentOptions.includes(PAYMENT_METHODS.PREPAY);

  const computedGuards = {
    allowJourneyMoveIn,
    meterValidationRequired: isMeterValidationRequired,
    hasAnyMeterValidationRequired,
    isPrePayOnly,
    isElectricity: fuelType.electric,
    isGas: fuelType.gas,
    isNewToBrand,
    isBGCustomer,
    isBGECustomer,
    isRelatedElectricityMeter,
    hasMixedMeters,
    isMonthlyDirectDebit,
    isMoveInEligible: !!isMoveInEligible,
    isAnyDirectDebit,
    isUpsellEnabled,
    isUpsellEnabledMeterValidationRequired,
    hasMultipleDirectDebitTypes,
    hasPSR,
    isNepSplitSupply,
    isSapSplitSupply,
    isMixedNepSap,
    showEvTariffInformation,
    hasMeterExceptions,
    allowAbandonedCart,
    hasElectricityTOUMeter
  };

  const { actor } = stateMachineContext;

  if (!shallowEqual(currentGuardsRef.current, computedGuards)) {
    // sync guards with machine context
    actor?.send({ type: 'UPDATE_GUARDS', guards: computedGuards });
    currentGuardsRef.current = computedGuards;
  }

  return computedGuards;
};
